import {
  capitalize,
  Divider,
  MenuItem,
  MenuItemProps,
  SxProps,
  Typography,
} from "@mui/material";
import { ChangeEvent, ReactNode, Suspense, memo } from "react";
import { Await } from "react-router";
import FormFieldSkeleton from "components/skeletons/FormField";
import { useTranslation } from "react-i18next";
import { TextValidator } from "react-material-ui-form-validator";

export interface AuturaDropdownProps {
  id?: string;
  sx?: SxProps;
  name: string;
  className?: string;
  helperText?: string;
  variant?: "standard" | "outlined" | "filled";
  margin?: "none" | "dense" | "normal";
  disabled?: boolean;
  readonly?: boolean;
  label?: string;
  required?: boolean;
  fullWidth?: boolean;
  options: string[];
  resolve?: Promise<any>;
  placeholder?: string;
  initialValue?: string;
  value: string;
  setValue?: (e: ChangeEvent<HTMLInputElement>) => void;
  startAdornment?: ReactNode;
  notched?: boolean;
  labelTextAlign?: "left" | "right" | "center";
  // menu item props
  menuItemProps?: MenuItemProps;
  specialOptions?: Array<{ value: string; label: string; disabled?: boolean }>;
}

const AuturaDropdown = ({
  sx,
  id,
  variant = "outlined",
  margin = "none",
  disabled = false,
  readonly = false,
  label,
  fullWidth = true,
  options,
  resolve,
  placeholder,
  value,
  setValue,
  required = false,
  name,
  menuItemProps,
  specialOptions,
  initialValue,
  startAdornment,
  className,
  helperText,
  notched,
  labelTextAlign = "left",
}: AuturaDropdownProps): ReactNode => {
  const { t } = useTranslation();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (setValue) {
      setValue(e);
    }
  };

  return (
    <Suspense
      fallback={
        <FormFieldSkeleton
          initialValue={initialValue}
          label={label}
          variant={variant}
          required={true}
          margin={margin}
          select
        />
      }
    >
      <Await resolve={resolve}>
        <TextValidator
          sx={sx}
          margin={margin}
          variant={variant}
          fullWidth={fullWidth}
          id={id || `${name}-select`}
          label={`${label} ${required ? "*" : ""}`}
          placeholder={placeholder}
          name={name}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          validators={required ? ["required"] : []}
          errorMessages={required ? [t("required")] : []}
          InputLabelProps={{ shrink: placeholder ? true : undefined }}
          SelectProps={{
            readOnly: readonly,
          }}
          InputProps={{
            notched: notched,
            startAdornment: startAdornment,
          }}
          inputProps={{
            style: { textAlign: "right" },
          }}
          select
          className={className}
          helperText={helperText}
        >
          {specialOptions &&
            specialOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                {...menuItemProps}
                disabled={option.disabled}
                divider
              >
                <Typography
                  sx={{ fontWeight: "bold", textAlign: labelTextAlign }}
                  variant="body1"
                >
                  {t(option.label)}
                </Typography>
              </MenuItem>
            ))}
          {specialOptions && <Divider sx={{ mb: 1 }} />}
          {options.map((option = "") => (
            <MenuItem key={option} value={option} {...menuItemProps} divider>
              <Typography sx={{ textAlign: labelTextAlign }} variant="body1">
                {capitalize(option)}
              </Typography>
            </MenuItem>
          ))}
        </TextValidator>
      </Await>
    </Suspense>
  );
};

export default memo(AuturaDropdown);
